import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Radio,
  Avatar,
  Tooltip,
  Stack,
  Skeleton,
  Alert,
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';

/**
 * 개선된 결재선 선택 컴포넌트 (V2)
 * 
 * 개선사항:
 * - 카드형 UI로 선택 영역 명확화
 * - 결재 흐름을 화살표로 시각화
 * - 선택 상태 시각적 강조
 * - 로딩 상태 Skeleton UI
 */
const ApprovalLineFormV2 = ({ 
  approvalLines, 
  apprUuid, 
  setApprUuid, 
  loading = false,
  error = null,
}) => {
  const handleSelect = (uuid) => {
    setApprUuid(uuid);
  };

  // 로딩 상태
  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        {[1, 2].map(i => (
          <Card key={i} variant="outlined" sx={{ mb: 1.5 }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <Skeleton variant="circular" width={20} height={20} />
                <Skeleton variant="text" width={120} />
              </Stack>
              <Stack direction="row" spacing={2} sx={{ pl: 4 }}>
                {[1, 2, 3].map(j => (
                  <React.Fragment key={j}>
                    {j > 1 && <Skeleton variant="text" width={20} />}
                    <Box sx={{ textAlign: 'center' }}>
                      <Skeleton variant="circular" width={40} height={40} />
                      <Skeleton variant="text" width={50} sx={{ mt: 0.5 }} />
                    </Box>
                  </React.Fragment>
                ))}
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  }

  // 에러 상태
  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        결재선을 불러오는데 실패했습니다. 다시 시도해주세요.
      </Alert>
    );
  }

  // 데이터 없음
  if (!approvalLines || approvalLines.length === 0) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        등록된 결재선이 없습니다. 결재선을 먼저 등록해주세요.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
        결재선을 선택하세요
      </Typography>

      {approvalLines.map((line) => {
        const isSelected = apprUuid === line.apprUuid;
        
        // 결재자 목록 추출
        const approvers = [];
        for (let i = 1; i <= 5; i++) {
          const name = line[`usrUuid${i}Nm`];
          const profile = line[`usrUuid${i}Profile`];
          const uuid = line[`usrUuid${i}`];
          if (name) {
            approvers.push({ name, profile, uuid, order: i });
          }
        }

        return (
          <Card
            key={line.apprUuid}
            variant="outlined"
            onClick={() => handleSelect(line.apprUuid)}
            sx={{
              mb: 1.5,
              cursor: 'pointer',
              borderColor: isSelected ? 'primary.main' : 'divider',
              borderWidth: isSelected ? 2 : 1,
              bgcolor: isSelected ? 'primary.50' : 'transparent',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: isSelected ? 'primary.main' : 'primary.light',
                bgcolor: isSelected ? 'primary.50' : 'action.hover',
              },
            }}
          >
            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
              {/* 결재선 이름 */}
              <Stack 
                direction="row" 
                spacing={1} 
                alignItems="center" 
                sx={{ mb: 2 }}
              >
                <Radio 
                  checked={isSelected} 
                  size="small"
                  sx={{ p: 0 }}
                />
                <Typography variant="subtitle1" fontWeight={600}>
                  {line.apprNm}
                </Typography>
                {isSelected && (
                  <CheckCircleIcon color="primary" fontSize="small" />
                )}
              </Stack>

              {/* 결재자 흐름 */}
              <Stack 
                direction="row" 
                spacing={1.5} 
                alignItems="center" 
                sx={{ pl: 4, flexWrap: 'wrap', gap: 1 }}
              >
                {approvers.map((approver, index) => (
                  <React.Fragment key={approver.uuid || index}>
                    {index > 0 && (
                      <ArrowForwardIcon 
                        color="action" 
                        fontSize="small" 
                        sx={{ opacity: 0.5 }}
                      />
                    )}
                    <Tooltip 
                      title={`${approver.order}차 결재자`}
                      arrow
                      placement="top"
                    >
                      <Box sx={{ textAlign: 'center', minWidth: 60 }}>
                        <Avatar 
                          src={approver.profile} 
                          sx={{ 
                            width: 40, 
                            height: 40, 
                            mb: 0.5,
                            mx: 'auto',
                            border: isSelected ? '2px solid' : 'none',
                            borderColor: 'primary.main',
                          }}
                        >
                          {approver.name?.[0]}
                        </Avatar>
                        <Typography 
                          variant="caption" 
                          display="block"
                          fontWeight={isSelected ? 500 : 400}
                        >
                          {approver.name}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          display="block"
                          sx={{ fontSize: '0.65rem' }}
                        >
                          {approver.order}차
                        </Typography>
                      </Box>
                    </Tooltip>
                  </React.Fragment>
                ))}
              </Stack>
            </CardContent>
          </Card>
        );
      })}

      {/* 선택 안내 */}
      {!apprUuid && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          결재선을 선택해야 저장할 수 있습니다.
        </Alert>
      )}
    </Box>
  );
};

export default ApprovalLineFormV2;
